import { measureTextWidth } from "../measureTextWidth";

import type { CellPair } from "@platform/formatting-utils/financials";
import type { FinancialsRow } from "../types";
import type { ICellRendererParams } from "ag-grid-community";

// Decides whether a variance / dollar-savings pair must stack onto two lines:
// true only when the inline "n | m" form genuinely wouldn't fit the current
// column width (a single-leg pair always fits). Uses the real column width,
// the cell's own padding, and a canvas text measurement — a pure function of
// width + content, re-evaluated whenever the cell re-renders on resize. Kept
// tight (no headroom) to favor the denser inline layout wherever it fits.
export const shouldStackPair = (
  pair: CellPair,
  formatLeg: (value: number) => string,
  params: ICellRendererParams<FinancialsRow>,
): boolean => {
  const { toCurrent, toOther } = pair;
  if (toCurrent === null || toOther === null) return false;
  if (!params.column) return false;

  const style = getComputedStyle(params.eGridCell);
  const font = style.font || `${style.fontSize} ${style.fontFamily}`;
  const paddingX =
    parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
  const available = params.column.getActualWidth() - paddingX;

  const inline = `${formatLeg(toCurrent)} | ${formatLeg(toOther)}`;

  return measureTextWidth(inline, font) > available;
};
